import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SingleSkuGroup } from '../../schemas/apiResponse.schemas';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchSingleSkuSummary,
  fetchTags,
  reserveOrders,
  releaseOrders,
} from '../../services/shipStationAPI';
import ShipOrdersModal from './ShipOrdersModal';
import { fetchOrderShelfLocations, fetchPrefixMap } from '../../services/skuShelfIndexAPI';
import { getLabelStatus, getLabelByStorageIdPrefix } from '../../services/shelfIndexAPI';
import { getTagNameFromEmail, tagDictionaryMaker } from '../../contexts/TagStorage';
import { getApiErrorMessage } from '../../services/errorUtils';
import { performOCR } from '../../services/visionAPI';
import { normalizeSku } from '../../utils/normalizeSku';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import ProductImage from '../shared/ProductImage';
import ProductInfoModal from '../shared/ProductInfoModal';
import { ScannerSection, SCAN_SUCCESS_FLASH_MS, type ScannerSectionHandle } from '../scanner';
import {
  isFadingQrScanError,
  QR_MISS_FADE_MS,
  QR_NOT_FOUND_RETRY_MESSAGE,
} from '../scanner/qrScanMessages';
import { QrPlaceholderIcon } from '../scanner/WebcamPlaceholder';
import OrderDetailCameraBar, { CameraIcon } from './orderdetail/OrderDetailCameraBar';
import {
  extractStorageIdFromQr,
  ocrTextMatchesExpectedSku,
} from './orderdetail/scanningLogic';
import { compareShelfIndices } from './orderdetail/shelfUtils';
import { recordFulfillmentEventsForOrders } from '../../services/fulfillmentEventsAPI';
import type { OrderShelfLocationMap } from '../../types/apiTypes';
import { useOrderAssignmentRealtime } from '../../hooks/useOrderAssignmentRealtime';
import {
  SCAN_SESSION_CLEARED_EVENT,
  loadSingleSkuScannedSkus,
  writeSingleSkuScannedSkus,
} from '../../utils/scanSessionCache';
import './OrdersList.css';
import './orderdetail/OrderDetail.css';
import './SingleSkuOrdersList.css';

function ShippedCheckIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M9.2 16.6 4.7 12.1 3 13.8l6.2 6.2 12-12-1.7-1.7z"
      />
    </svg>
  );
}

type ShelfType = 'pick bin' | 'stock';

interface SingleSkuGroupView extends SingleSkuGroup {
  readonly shelfIndex: string | null;
  readonly shelfType: ShelfType | null;
}

function normalizeShelfType(type: string | null | undefined): ShelfType | null {
  if (type === 'pick bin' || type === 'stock') {
    return type;
  }
  return null;
}

function sortSingleSkuGroups(
  groups: readonly SingleSkuGroupView[],
): SingleSkuGroupView[] {
  return [...groups].sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }
    const shelfCmp = compareShelfIndices(left.shelfIndex, right.shelfIndex);
    if (shelfCmp !== 0) {
      return shelfCmp;
    }
    const leftName = left.itemName.trim() || left.sku;
    const rightName = right.itemName.trim() || right.sku;
    const nameCmp = leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    if (nameCmp !== 0) {
      return nameCmp;
    }
    return left.sku.localeCompare(right.sku, undefined, { sensitivity: 'base' });
  });
}

function renderShelfLocation(
  shelfIndex: string | null,
  shelfType: ShelfType | null,
): React.ReactNode {
  if (!shelfIndex) {
    return 'N/A';
  }
  if (shelfType === 'stock') {
    return (
      <>
        <span className="single-sku-shelf-stock-label">Stock:</span>
        <span>{shelfIndex}</span>
      </>
    );
  }
  return shelfIndex;
}

function withReservedBy(
  groups: readonly SingleSkuGroupView[],
  sku: string,
  reservedOrderIds: ReadonlySet<string>,
  reservedBy: string,
): SingleSkuGroupView[] {
  return groups.map((group) => {
    if (group.sku !== sku) {
      return group;
    }
    return {
      ...group,
      orders: group.orders.map((order) =>
        reservedOrderIds.has(order.orderId) ? { ...order, reservedBy } : order,
      ),
    };
  });
}

function withClearedReservedBy(
  groups: readonly SingleSkuGroupView[],
  sku: string,
  orderIds: ReadonlySet<string>,
): SingleSkuGroupView[] {
  return groups.map((group) => {
    if (group.sku !== sku) {
      return group;
    }
    return {
      ...group,
      orders: group.orders.map((order) =>
        orderIds.has(order.orderId) ? { ...order, reservedBy: null } : order,
      ),
    };
  });
}

function withoutOrderIds(
  groups: readonly SingleSkuGroupView[],
  orderIds: ReadonlySet<string>,
): SingleSkuGroupView[] {
  if (orderIds.size === 0) {
    return [...groups];
  }
  return groups.flatMap((group) => {
    const orders = group.orders.filter((order) => !orderIds.has(order.orderId));
    if (orders.length === group.orders.length) {
      return [group];
    }
    if (orders.length === 0) {
      return [];
    }
    return [{ ...group, orders, count: orders.length }];
  });
}

function groupFullyReserved(group: SingleSkuGroupView): boolean {
  return group.orders.length > 0 && group.orders.every((order) => Boolean(order.reservedBy));
}

function SingleSkuOrdersList(): React.ReactElement {
  const { user } = useAuth();
  const [groups, setGroups] = useState<readonly SingleSkuGroupView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [shipGroup, setShipGroup] = useState<SingleSkuGroupView | null>(null);
  const [productInfoItem, setProductInfoItem] = useState<{
    sku: string;
    itemName?: string;
    name?: string;
  } | null>(null);
  const [reservingSku, setReservingSku] = useState<string | null>(null);
  const [scannedSkus, setScannedSkus] = useState<ReadonlySet<string>>(
    () => new Set(loadSingleSkuScannedSkus()),
  );
  const [shippedSkus, setShippedSkus] = useState<ReadonlySet<string>>(() => new Set());
  const [scanningSku, setScanningSku] = useState<string | null>(null);
  const [webcamOn, setWebcamOn] = useState(false);
  const [qrActive, setQrActive] = useState(false);
  const [prefixToSkuMap, setPrefixToSkuMap] = useState<Record<string, string>>({});
  const [showScanSuccess, setShowScanSuccess] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const scanInFlightRef = useRef(false);
  const scanSuccessTimerRef = useRef<number | null>(null);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const qrMissFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupsRef = useRef<readonly SingleSkuGroupView[]>([]);
  const viewerTagIdRef = useRef<number | null>(null);
  groupsRef.current = groups;
  const sortedGroups = useMemo(() => sortSingleSkuGroups(groups), [groups]);

  useEffect(() => {
    if (qrMissFadeTimerRef.current) {
      clearTimeout(qrMissFadeTimerRef.current);
      qrMissFadeTimerRef.current = null;
    }
    if (!actionError || !isFadingQrScanError(actionError)) {
      return undefined;
    }
    qrMissFadeTimerRef.current = setTimeout(() => {
      qrMissFadeTimerRef.current = null;
      setActionError(null);
    }, QR_MISS_FADE_MS);
    return () => {
      if (qrMissFadeTimerRef.current) {
        clearTimeout(qrMissFadeTimerRef.current);
        qrMissFadeTimerRef.current = null;
      }
    };
  }, [actionError]);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setLoadError(null);
      try {
        const tagsResponse = await fetchTags({ forceRefresh: true });
        if (cancelled) return;
        const tagDictionary = tagDictionaryMaker(tagsResponse);
        const tagName = getTagNameFromEmail(user?.email ?? '', tagDictionary);
        const userTag = tagName ? tagDictionary[tagName] : undefined;
        const viewerTag =
          typeof userTag === 'number' || typeof userTag === 'string' ? userTag : undefined;
        viewerTagIdRef.current = typeof userTag === 'number' ? userTag : null;

        const data = await fetchSingleSkuSummary(viewerTag);
        if (cancelled) return;

        const skus = data.groups.map((group) => group.sku);
        const shelfMap: OrderShelfLocationMap = await fetchOrderShelfLocations(skus).catch(
          (): OrderShelfLocationMap => ({}),
        );
        if (cancelled) return;

        setGroups(
          data.groups.map((group) => {
            const loc = shelfMap[group.sku] ?? shelfMap[normalizeSku(group.sku)];
            return {
              ...group,
              shelfIndex: loc?.shelfIndex ?? null,
              shelfType: normalizeShelfType(loc?.type),
            };
          }),
        );
      } catch (err: unknown) {
        if (cancelled) return;
        setGroups([]);
        setLoadError(getApiErrorMessage(err, 'Failed to load single-item SKU summary'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => {
    return () => {
      if (scanSuccessTimerRef.current != null) {
        window.clearTimeout(scanSuccessTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onCleared = (): void => {
      setScannedSkus(new Set());
      setShippedSkus(new Set());
    };
    window.addEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
  }, []);

  const closeScanSession = (): void => {
    setScanningSku(null);
    setWebcamOn(false);
    setQrActive(false);
    setPrefixToSkuMap({});
  };

  const finishSuccessfulScan = (sku: string): void => {
    setScannedSkus((current) => {
      const next = new Set(current).add(sku);
      writeSingleSkuScannedSkus(next);
      return next;
    });
    setActionMessage(`SKU ${sku} matched.`);
    setActionError(null);
    window.setTimeout(() => {
      closeScanSession();
      setShowScanSuccess(true);
      if (scanSuccessTimerRef.current != null) {
        window.clearTimeout(scanSuccessTimerRef.current);
      }
      scanSuccessTimerRef.current = window.setTimeout(() => {
        setShowScanSuccess(false);
        scanSuccessTimerRef.current = null;
      }, 1000);
    }, SCAN_SUCCESS_FLASH_MS);
    const group = groups.find((entry) => entry.sku === sku);
    const orderIds = group?.orders.map((order) => order.orderId).filter(Boolean) ?? [];
    if (orderIds.length > 0) {
      void recordFulfillmentEventsForOrders(orderIds, { sku });
    }
  };

  const applyClearedReservedBy = (sku: string, orderIds: ReadonlyArray<string>): void => {
    const idSet = new Set(orderIds);
    setGroups((current) => withClearedReservedBy(current, sku, idSet));
  };

  const clearSkuScanProgress = (sku: string): void => {
    if (scanningSku === sku) {
      closeScanSession();
    }
    setScannedSkus((current) => {
      if (!current.has(sku)) {
        return current;
      }
      const next = new Set(current);
      next.delete(sku);
      writeSingleSkuScannedSkus(next);
      return next;
    });
    setShippedSkus((current) => {
      if (!current.has(sku)) {
        return current;
      }
      const next = new Set(current);
      next.delete(sku);
      return next;
    });
  };

  const markSkuShipped = (sku: string): void => {
    setShippedSkus((current) => {
      if (current.has(sku)) {
        return current;
      }
      return new Set(current).add(sku);
    });
  };

  useOrderAssignmentRealtime({
    onAssigned: (event) => {
      const viewerTagId = viewerTagIdRef.current;
      if (event.userTagId != null && viewerTagId != null && event.userTagId === viewerTagId) {
        return;
      }
      const orderId = event.orderId;
      const taken = new Set([orderId]);
      const affected = groupsRef.current.filter((group) =>
        group.orders.some((order) => order.orderId === orderId || String(order.orderId) === orderId),
      );
      if (affected.length === 0) {
        return;
      }
      setGroups((current) => withoutOrderIds(current, taken));
      setShipGroup((current) => {
        if (current == null) {
          return current;
        }
        return withoutOrderIds([current], taken)[0] ?? null;
      });
      for (const group of affected) {
        const remaining = group.orders.filter((order) => order.orderId !== orderId);
        if (remaining.length === 0) {
          clearSkuScanProgress(group.sku);
          setExpandedSku((current) => (current === group.sku ? null : current));
        }
      }
    },
  });

  const handleRowClick = (sku: string): void => {
    setExpandedSku((current) => (current === sku ? null : sku));
    closeScanSession();
    setActionMessage(null);
    setActionError(null);
  };

  const openShipModal = (group: SingleSkuGroupView): void => {
    if (!groupFullyReserved(group) || !scannedSkus.has(group.sku)) {
      return;
    }
    setShipGroup(group);
  };

  const handleToggleScanItem = (sku: string): void => {
    setActionMessage(null);
    setActionError(null);
    if (scanningSku === sku) {
      closeScanSession();
      return;
    }
    setScanningSku(sku);
    setWebcamOn(true);
    setQrActive(false);
    void fetchPrefixMap([sku]).then(setPrefixToSkuMap);
  };

  const handleScanCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc || !scanningSku || scanInFlightRef.current) {
      return false;
    }
    const expectedSku = scanningSku;
    scanInFlightRef.current = true;
    setActionMessage('Processing image...');
    setActionError(null);
    try {
      const ocrText = await performOCR(imageSrc);
      if (!ocrText) {
        setActionError('Error processing image. Please try again.');
        setActionMessage(null);
        return false;
      }
      if (!ocrTextMatchesExpectedSku(ocrText, expectedSku)) {
        setActionError(`SKU did not match ${expectedSku}. Scan input: ${ocrText}`);
        setActionMessage(null);
        return false;
      }
      finishSuccessfulScan(expectedSku);
      return true;
    } finally {
      scanInFlightRef.current = false;
    }
  };

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    if (!qrData || !scanningSku || scanInFlightRef.current) {
      return false;
    }
    const expectedSku = scanningSku;
    scanInFlightRef.current = true;
    setActionMessage(null);
    setActionError(null);
    try {
      const prefix = extractStorageIdFromQr(qrData);
      if (!prefix) {
        setActionError(QR_NOT_FOUND_RETRY_MESSAGE);
        return false;
      }
      let sku: string | null = prefixToSkuMap[prefix] ?? null;
      if (!sku) {
        setActionMessage('Looking up label...');
        const status = await getLabelStatus(prefix);
        if (status.found && !status.checkedIn) {
          setActionError('Storage ID is checked out');
          setActionMessage(null);
          return false;
        }
        if (!status.found) {
          setActionError('Label not found in database.');
          setActionMessage(null);
          return false;
        }
        const label = await getLabelByStorageIdPrefix(prefix);
        sku = label?.sku ?? null;
      }
      if (!sku || !ocrTextMatchesExpectedSku(sku, expectedSku)) {
        setActionError(`SKU did not match ${expectedSku}.`);
        setActionMessage(null);
        return false;
      }
      finishSuccessfulScan(expectedSku);
      return true;
    } finally {
      scanInFlightRef.current = false;
    }
  };

  const closeShipModal = (): void => {
    setShipGroup(null);
  };

  const resolveViewerPickTag = async (): Promise<{
    tagName: string;
    userTag: number;
  } | null> => {
    const tagsResponse = await fetchTags({ forceRefresh: true });
    const tagDictionary = tagDictionaryMaker(tagsResponse);
    const resolvedTagName = getTagNameFromEmail(user?.email ?? '', tagDictionary);
    const userTag = resolvedTagName ? tagDictionary[resolvedTagName] : undefined;
    if (resolvedTagName == null || typeof userTag !== 'number') {
      setActionError(`No tag found for user: ${resolvedTagName ?? user?.email ?? 'unknown'}`);
      return null;
    }
    return { tagName: resolvedTagName, userTag };
  };

  const handleReserveOrders = async (group: SingleSkuGroupView): Promise<void> => {
    setActionMessage(null);
    setActionError(null);

    const ids = group.orders.map((order) => order.orderId).filter(Boolean);
    if (ids.length === 0) {
      setActionError('No orders to reserve');
      return;
    }

    setReservingSku(group.sku);
    try {
      const viewer = await resolveViewerPickTag();
      if (viewer == null) {
        return;
      }

      const result = await reserveOrders(ids, viewer.userTag);
      const taggedOrderIds = result.results
        .filter((entry) => entry.success && !entry.takenByOther)
        .map((entry) => entry.orderId);
      const takenOrderIds = result.results
        .filter((entry) => entry.takenByOther)
        .map((entry) => entry.orderId);
      const takenCount = result.takenByOther ?? takenOrderIds.length;
      const takenSet = new Set(takenOrderIds);

      setGroups((current) => {
        let next = current;
        if (takenSet.size > 0) {
          next = withoutOrderIds(next, takenSet);
        }
        if (taggedOrderIds.length > 0) {
          next = withReservedBy(next, group.sku, new Set(taggedOrderIds), viewer.tagName);
        }
        return next;
      });

      if (takenSet.size > 0) {
        const remaining = group.orders.filter((order) => !takenSet.has(order.orderId));
        if (remaining.length === 0) {
          clearSkuScanProgress(group.sku);
          setExpandedSku((current) => (current === group.sku ? null : current));
          setShipGroup((current) => (current?.sku === group.sku ? null : current));
        }
        setActionError(
          `${takenCount} orders for item ${group.sku} have been reserved by another user`,
        );
      }

      const skipped = result.skipped ?? 0;
      if (result.tagged > 0 || skipped > 0) {
        setActionMessage(
          skipped > 0
            ? `Reserved ${result.tagged} of ${result.total} ${group.sku} orders (${skipped} already tagged)`
            : `Reserved ${result.tagged} of ${result.total} ${group.sku} orders with tag "${viewer.tagName}"`,
        );
      }
      if (takenCount === 0 && !result.success) {
        setActionError('Some orders failed to reserve');
      }
    } catch (err: unknown) {
      setActionError(getApiErrorMessage(err, 'Failed to reserve orders'));
    } finally {
      setReservingSku(null);
    }
  };

  const handleReleaseOrders = async (group: SingleSkuGroupView): Promise<void> => {
    setActionMessage(null);
    setActionError(null);

    const ids = group.orders.map((order) => order.orderId).filter(Boolean);
    if (ids.length === 0) {
      setActionError('No orders to release');
      return;
    }

    setReservingSku(group.sku);
    try {
      const viewer = await resolveViewerPickTag();
      if (viewer == null) {
        return;
      }

      const result = await releaseOrders(ids, viewer.userTag);
      const releasedIds = result.results
        .filter((entry) => entry.success)
        .map((entry) => entry.orderId);
      if (releasedIds.length > 0) {
        applyClearedReservedBy(group.sku, releasedIds);
        clearSkuScanProgress(group.sku);
      }
      setActionMessage(
        `Released ${result.released} of ${result.total} ${group.sku} orders`,
      );
      if (!result.success) {
        setActionError('Some orders failed to release');
      }
    } catch (err: unknown) {
      setActionError(getApiErrorMessage(err, 'Failed to release orders'));
    } finally {
      setReservingSku(null);
    }
  };

  const listStatus =
    !loadError && !actionMessage && !actionError && loading ? 'Loading...' : null;
  const showEmpty = !loading && !loadError && sortedGroups.length === 0;

  return (
    <div className={`orders-container single-sku-page${scanningSku ? ' single-sku-scanning' : ''}${webcamOn ? ' single-sku-camera-open' : ''}`}>
      <header className="single-sku-header">
        <div className="single-sku-header-status" role="status" aria-live="polite">
          {loadError ? (
            <p className="single-sku-error">{loadError}</p>
          ) : null}
          {actionMessage ? (
            <p className="single-sku-action-message">{actionMessage}</p>
          ) : null}
          {actionError && !(webcamOn && isFadingQrScanError(actionError)) ? (
            <p className="single-sku-error">{actionError}</p>
          ) : null}
          {listStatus ? <p className="single-sku-list-status">{listStatus}</p> : null}
        </div>
      </header>
      <div className={`single-sku-body${showEmpty ? ' single-sku-body-empty' : ''}`}>
      {showEmpty ? (
        <h2 className="single-sku-empty-title">No Single-Item Orders</h2>
      ) : !loading ? (
        <ul className="single-sku-groups">
          {sortedGroups.map((group) => {
            const isExpanded = expandedSku === group.sku;
            const isReserving = reservingSku === group.sku;
            const isScanning = scanningSku === group.sku;
            const allReserved = groupFullyReserved(group);
            const isShipped = shippedSkus.has(group.sku);
            const canShip = !isShipped && allReserved && scannedSkus.has(group.sku);
            const rowStateClass = isShipped
              ? ' single-sku-row-shipped'
              : canShip
                ? ' single-sku-row-ready-ship'
                : allReserved
                  ? ' single-sku-row-ready-scan'
                  : '';
            return (
              <li key={group.sku} className="single-sku-group-item">
                <div className="single-sku-parent">
                  <button
                    type="button"
                    className={`single-sku-order-circle ${allReserved ? 'single-sku-order-circle-release' : 'single-sku-order-circle-reserve'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (allReserved) {
                        void handleReleaseOrders(group);
                      } else {
                        void handleReserveOrders(group);
                      }
                    }}
                    disabled={isReserving}
                    aria-label={allReserved ? `Release ${group.sku} orders` : `Reserve ${group.sku} orders`}
                  >
                    {isReserving ? '…' : (allReserved ? 'Release' : 'Reserve')}
                  </button>
                  <div
                    className={`order-box-container single-sku-row${isExpanded ? ' single-sku-row-expanded' : ''}${rowStateClass}`}
                  >
                    <button
                      type="button"
                      className="single-sku-thumb"
                      onClick={(event) => {
                        event.stopPropagation();
                        setProductInfoItem({
                          sku: group.sku,
                          itemName: group.itemName || undefined,
                          name: group.itemName || undefined,
                        });
                      }}
                      aria-label={`Product details for ${group.sku}`}
                    >
                      <ProductImage
                        sku={group.sku}
                        alt={group.itemName || group.sku}
                      />
                    </button>
                    <button
                      type="button"
                      className="single-sku-row-main"
                      onClick={() => handleRowClick(group.sku)}
                      aria-expanded={isExpanded}
                    >
                      <div className="single-sku-left">
                        <div className="single-sku-text">
                          <div className="single-sku-sku">{renderSkuWithSoftBreaks(group.sku)}</div>
                          {group.itemName?.trim() ? (
                            <div className="single-sku-name">{group.itemName.trim()}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className="single-sku-count-block">
                        <span className="single-sku-count">{group.count}</span>
                      </div>
                      <div className="single-sku-shelf">
                        {renderShelfLocation(group.shelfIndex, group.shelfType)}
                      </div>
                      <span
                        className={`single-sku-chevron${isExpanded ? ' single-sku-chevron-expanded' : ''}`}
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
                          <path
                            d="M9 5 L16 12 L9 19"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                  <div className="single-sku-parent-right">
                    {isShipped ? (
                      <span className="single-sku-shipped-check" role="img" aria-label={`Shipped ${group.sku}`}>
                        <ShippedCheckIcon />
                      </span>
                    ) : canShip ? (
                      <button
                        type="button"
                        className="single-sku-order-ship"
                        onClick={(event) => {
                          event.stopPropagation();
                          openShipModal(group);
                        }}
                        aria-label={`Parcel ${group.sku}`}
                      >
                        Parcel
                      </button>
                    ) : allReserved ? (
                      <button
                        type="button"
                        className={`single-sku-order-circle single-sku-order-circle-scan${isScanning ? ' single-sku-order-circle-scan-active' : ''}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggleScanItem(group.sku);
                        }}
                        aria-pressed={isScanning}
                        aria-label={isScanning ? `Cancel scan for ${group.sku}` : `Scan item ${group.sku}`}
                      >
                        <CameraIcon size={18} />
                      </button>
                    ) : null}
                  </div>
                </div>
                {isExpanded ? (
                  <div className="single-sku-batch-panel">
                    <ul className="single-sku-batch-list">
                      {group.orders.map((order) => (
                        <li key={`${group.sku}-${order.orderNumber}`} className="single-sku-batch-item">
                          <div className="single-sku-batch-order">{order.orderNumber || '—'}</div>
                          <div className="single-sku-batch-name">
                            {order.customerName || '—'}
                          </div>
                          {order.reservedBy ? (
                            <div className="single-sku-batch-reserved-by">{order.reservedBy}</div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      </div>

      {scanningSku ? (
        <OrderDetailCameraBar
          open={webcamOn}
          onShutter={() => scannerCaptureRef.current?.capture()}
          leftActions={
            <div className="order-detail-bottom-bar-left-group">
              <button
                type="button"
                className="order-detail-scan-mode-toggle"
                onClick={() => setQrActive((prev) => !prev)}
                aria-pressed={qrActive}
                title={qrActive ? 'Switch to text (OCR) scan' : 'Switch to QR code scan'}
                aria-label={qrActive ? 'Scan mode: QR. Switch to text.' : 'Scan mode: text. Switch to QR.'}
              >
                <span className={`order-detail-scan-mode-option${qrActive ? ' active' : ''}`}>
                  <QrPlaceholderIcon />
                </span>
                <span className={`order-detail-scan-mode-option${!qrActive ? ' active' : ''}`}>
                  <span className="placeholder-text-chip">a..z</span>
                </span>
              </button>
              <button
                type="button"
                className={`order-detail-bar-camera-btn${webcamOn ? ' open' : ''}`}
                onClick={() => setWebcamOn((prev) => !prev)}
                aria-pressed={webcamOn}
                aria-label={webcamOn ? 'Close camera' : 'Open camera'}
              >
                <CameraIcon size={28} />
              </button>
            </div>
          }
        >
          <ScannerSection
            ref={scannerCaptureRef}
            mode={qrActive ? 'qr' : 'barcode'}
            onCapture={qrActive ? handleQrCapture : handleScanCapture}
            webcamOn={webcamOn}
            setWebcamOn={setWebcamOn}
            className="webcam-capture-container"
            overlay={
              webcamOn && actionError && isFadingQrScanError(actionError) ? (
                <div
                  className="order-detail-message order-detail-message-error webcam-qr-miss-fade"
                  role="status"
                >
                  {actionError}
                </div>
              ) : null
            }
          />
        </OrderDetailCameraBar>
      ) : null}

      {showScanSuccess ? (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}

      {shipGroup ? (
        <ShipOrdersModal
          subtitle={`${shipGroup.sku} · ${shipGroup.orders.length} identical packages`}
          orders={shipGroup.orders.map((order) => ({
            orderId: order.orderId,
            orderNumber: order.orderNumber,
          }))}
          sku={shipGroup.sku}
          onClose={closeShipModal}
          onFullyShipped={() => markSkuShipped(shipGroup.sku)}
        />
      ) : null}

      {productInfoItem ? (
        <ProductInfoModal
          item={productInfoItem}
          onClose={() => setProductInfoItem(null)}
        />
      ) : null}
    </div>
  );
}

export default SingleSkuOrdersList;
